import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, ShieldCheck, FileSignature, Clock, X, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public sign page — no login required. The customer arrives via a tokenised
 *  link emailed/whatsapped by an admin. We fetch the estimate, render line
 *  items, and let them sign or decline. */
export default function PublicEstimateSign() {
  const { token } = useParams();
  const [est, setEst] = useState(null);
  const [error, setError] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [signedEmail, setSignedEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const sigRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/estimate/${token}`)
      .then((r) => { if (!cancelled) setEst(r.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 410) setError("expired");
        else if (err?.response?.status === 404) setError("not_found");
        else setError("unknown");
      });
    return () => { cancelled = true; };
  }, [token]);

  if (error) return <ErrorScreen kind={error} />;
  if (!est) return <LoadingScreen />;
  if (done || est.status === "signed") return <SignedScreen est={est} />;
  if (est.status === "declined") return <DeclinedScreen est={est} />;

  const sign = async () => {
    if (!signedName.trim()) { toast.error("Please type your full name to sign."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please add your signature above."); return; }
    setSubmitting(true);
    try {
      const sig = sigRef.current.getCanvas().toDataURL("image/png");
      await axios.post(`${API}/public/estimate/${token}/sign`, {
        signature: sig,
        signed_name: signedName.trim(),
        signed_email: signedEmail.trim(),
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your signature. Please try again.");
    } finally { setSubmitting(false); }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/estimate/${token}/decline`, { reason: declineReason });
      setEst((s) => ({ ...s, status: "declined", decline_reason: declineReason, declined_at: new Date().toISOString() }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your response. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950">
      {/* Brand bar */}
      <header className="bg-[#0A2540] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="" className="w-9 h-9 rounded bg-white p-0.5" />
            <div>
              <div className="font-heading font-bold tracking-wider">POWERBUILT</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Solar · EV · Contracting</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-white/60">
            <ShieldCheck className="w-3.5 h-3.5 text-[#C9A356]" /> Encrypted signing
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="card-pb p-7 mb-5">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
            <div>
              <p className="overline">Estimate · {est.estimate_id}</p>
              <h1 className="font-heading text-3xl text-[#0A2540] dark:text-white mt-1">{est.title}</h1>
              <div className="text-sm text-[#475569] dark:text-slate-400 mt-1">Prepared for {est.client_name}</div>
            </div>
            {est.expires_at && (
              <div className="text-xs text-[#475569] dark:text-slate-400 inline-flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/15 px-2.5 py-1 rounded-full">
                <Clock className="w-3.5 h-3.5" /> Sign by {new Date(est.expires_at).toLocaleDateString()}
              </div>
            )}
          </div>

          <div className="overflow-x-auto mt-5">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-[#475569] dark:text-slate-400 border-b border-[#E2E8F0] dark:border-slate-700">
                <tr>
                  <th className="py-2 text-left">Description</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Unit</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {est.line_items.map((li, i) => {
                  const unit = li.locked_unit_price ?? li.effective_unit_price ?? li.unit_price;
                  const qty = li.locked_qty ?? li.qty;
                  const lineTotal = parseFloat(qty) * parseFloat(unit);
                  const snap = li.package_spec_snapshot;
                  return (
                    <React.Fragment key={`${li.description}-${i}`}>
                      <tr className="border-b border-[#E2E8F0] dark:border-slate-700 align-top">
                        <td className="py-2.5">
                          {li.description}
                          {snap && (
                            <div className="mt-1 text-[11px] text-[#475569] dark:text-slate-400 leading-relaxed">
                              <span className="inline-block mr-2 font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#FFFBF0] text-[#8A6B14]" data-testid={`snap-badge-${i}`}>
                                {snap.name} · v{snap.version} · locked-in price
                              </span>
                              {snap.system_size_kw && <span>· {snap.system_size_kw} kW </span>}
                              {snap.panels && <span>· {snap.panels} panels </span>}
                              {snap.warranty_years && <span>· {snap.warranty_years}-yr warranty </span>}
                              {snap.visits_per_year && <span>· {snap.visits_per_year} visits/yr</span>}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">{qty}</td>
                        <td className="py-2.5 text-right tabular-nums">${parseFloat(unit).toFixed(2)}</td>
                        <td className="py-2.5 text-right tabular-nums">${lineTotal.toFixed(2)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ml-auto max-w-xs space-y-1.5 text-sm mt-4">
            <Row label="Subtotal" value={`$${(est.subtotal || 0).toFixed(2)}`} />
            {est.discount > 0 && <Row label="Discount" value={`-$${(est.discount || 0).toFixed(2)}`} />}
            <Row label={`Tax (${((est.tax_rate || 0) * 100).toFixed(0)}%)`} value={`$${(est.tax_amount || 0).toFixed(2)}`} />
            <div className="pt-2 mt-2 border-t border-[#E2E8F0] dark:border-slate-700 flex justify-between font-bold text-[#0A2540] dark:text-white text-lg">
              <span>Total</span>
              <span className="tabular-nums">${(est.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>

          {est.scope_notes && (
            <div className="mt-6 pt-5 border-t border-[#E2E8F0] dark:border-slate-700">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#475569] dark:text-slate-400 mb-1.5">Scope of work</div>
              <p className="text-sm text-[#0A2540] dark:text-white whitespace-pre-wrap leading-relaxed">{est.scope_notes}</p>
            </div>
          )}
        </div>

        {/* Sign block */}
        <div className="card-pb p-7" data-testid="public-sign-block">
          <h2 className="font-heading text-xl text-[#0A2540] dark:text-white mb-3 flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-[#C9A356]" /> Approve & sign
          </h2>
          <p className="text-sm text-[#475569] dark:text-slate-400 mb-5">
            Type your name and sign below. By signing, you authorize {est.company_name} to begin work as outlined above.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Full name *" data-testid="public-sign-name"
                   className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2.5 text-sm" />
            <input type="email" value={signedEmail} onChange={(e) => setSignedEmail(e.target.value)} placeholder="Email (optional)" data-testid="public-sign-email"
                   className="border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2.5 text-sm" />
          </div>

          <div className="border-2 border-dashed border-[#C9A356]/40 rounded-md bg-white touch-none mb-2" data-testid="public-sig-pad">
            <SignatureCanvas ref={sigRef} canvasProps={{ width: 600, height: 180, className: "w-full" }} />
          </div>
          <button onClick={() => sigRef.current?.clear()} className="text-xs text-[#475569] hover:text-[#0A2540] dark:hover:text-white inline-flex items-center gap-1 mb-5" data-testid="public-sig-clear">
            <RotateCcw className="w-3 h-3" /> Clear signature
          </button>

          <div className="flex flex-wrap gap-2">
            <button onClick={sign} disabled={submitting} className="btn-accent flex-1 min-w-[200px]" data-testid="public-sign-submit">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {submitting ? "Signing…" : "Approve & sign estimate"}
            </button>
            <button onClick={() => setShowDecline(true)} className="btn-outline-navy text-sm" data-testid="public-decline-btn">
              <X className="w-4 h-4 mr-1" /> Decline
            </button>
          </div>

          <p className="text-[10px] text-[#94A3B8] mt-4 leading-relaxed">
            Your signature, name, IP address and timestamp are recorded as proof of consent. We don't store credit-card data on this page.
          </p>
        </div>

        <div className="text-center mt-6 text-xs text-[#94A3B8]">
          {est.company_name} · {est.company_email}{est.company_phone ? ` · ${est.company_phone}` : ""}
        </div>
      </main>

      {showDecline && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setShowDecline(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="decline-modal">
            <h3 className="font-heading text-xl text-[#0A2540] dark:text-white mb-2">Decline estimate?</h3>
            <p className="text-sm text-[#475569] dark:text-slate-400 mb-4">If you'd like, leave a quick reason — it helps us prepare a revised proposal.</p>
            <textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} rows={3} maxLength={1000}
                      placeholder="Optional: budget, timing, scope changes…"
                      data-testid="decline-reason"
                      className="w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-2 text-sm" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDecline(false)} className="btn-outline-navy text-sm">Cancel</button>
              <button onClick={decline} disabled={submitting} className="btn-primary text-sm" data-testid="decline-confirm">
                {submitting ? "Sending…" : "Decline estimate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[#475569] dark:text-slate-400">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function LoadingScreen() {
  return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]"><div className="text-[#475569]">Loading estimate…</div></div>;
}

function ErrorScreen({ kind }) {
  const map = {
    expired: { title: "This link has expired", body: "Please ask PowerBuilt to re-send a fresh sign link." },
    not_found: { title: "Estimate not found", body: "The link may have been revoked or signed already." },
    unknown: { title: "Something went wrong", body: "Please refresh, or contact us if it persists." },
  };
  const m = map[kind] || map.unknown;
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6">
      <div className="card-pb p-8 max-w-md text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h1 className="font-heading text-2xl text-[#0A2540]">{m.title}</h1>
        <p className="text-sm text-[#475569] mt-2">{m.body}</p>
      </div>
    </div>
  );
}

function SignedScreen({ est }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] dark:bg-slate-950 p-6">
      <div className="card-pb p-10 max-w-md text-center" data-testid="signed-success">
        <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full grid place-items-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="font-heading text-2xl text-[#0A2540] dark:text-white">Estimate signed ✓</h1>
        <p className="text-sm text-[#475569] dark:text-slate-400 mt-2">
          Thanks {est.signed_name?.split(" ")[0] || "for your trust"} — we'll be in touch shortly to confirm scheduling and next steps.
        </p>
        <div className="text-xs text-[#94A3B8] mt-4 font-mono">{est.estimate_id}</div>
      </div>
    </div>
  );
}

function DeclinedScreen({ est }) {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6">
      <div className="card-pb p-10 max-w-md text-center">
        <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h1 className="font-heading text-2xl text-[#0A2540]">Estimate declined</h1>
        <p className="text-sm text-[#475569] mt-2">No problem — our team will reach out to discuss alternatives.</p>
        {est.decline_reason && <p className="mt-3 italic text-sm text-[#475569]">"{est.decline_reason}"</p>}
      </div>
    </div>
  );
}
